import { computed, ref, watch, type Ref } from "vue";

import { useRouteContext, useRouterContext } from "@core/composables/state/useRouteContext";
import type { LocationQueryRaw, RouteLocationNormalizedLoaded, Router } from "vue-router";

import { useMapViewState } from "@/composables/useMapViewState";

export type ViewType =
  | "sign-in"
  | "home"
  | "payment"
  | "faq"
  | "faq-item"
  | "faq-section"
  | "customer-service"
  | "tour"
  | "youpark-plus"
  | "youpark-plus-terms"
  | "youpark-plus-start"
  | "youpark-plus-status"
  | "map"
  | "map-parking"
  | "map-toll"
  | "privacypolicy"
  | "terms"
  | "profile"
  | "vehicles"
  | "add-vehicle"
  | "edit-vehicle"
  | "payment-sources"
  | "add-payment-source"
  | "edit-payment-source"
  | "subscriptions"
  | "subscription-details"
  | "subscription-shared-details"
  | "subscription-purchase"
  | "subscription-renew"
  | "template-manager-invitation"
  | "shared-user-invitation"
  | "coupons"
  | "bookings"
  | "receipts"
  | "youpark"
  | "youpark-customer-register"
  | "youpark-subscriptions"
  | "youpark-invoices"
  | "youpark-transactions"
  | "youpark-credit"
  | "youpark-booking-reservations"
  | "youpark-free-visits"
  | "youpark-reports"
  | "youpark-insights"
  | "youpark-business-insights"
  | "youpark-todos"
  | "youpark-access"
  | "youpark-customer-admin"
  | "youpark-customer-admin-locations"
  | "youpark-customer-admin-locations-settings"
  | "youpark-customer-admin-locations-price-setup"
  | "youpark-customer-admin-locations-autopass"
  | "youpark-customer-admin-locations-display"
  | "youpark-customer-admin-locations-contactpersons"
  | "youpark-customer-admin-locations-cameras"
  | "youpark-customer-admin-locations-connections"
  | "youpark-customer-admin-locations-contract"
  | "youpark-customer-admin-locations-businessmodel"
  | "youpark-customer-admin-payment-kiosks"
  | "youpark-customer-admin-invoicing"
  | "youpark-customer-admin-areas"
  | "youpark-customer-admin-access"
  | "youpark-customer-admin-free-visit-kiosks"
  | "youpark-customer-admin-fees"
  | "cameras-overview"
  | "users"
  | "remit"
  | "remit-tab"
  | "remit-allocations"
  | "remit-creditors"
  | "user-create"
  | "user-edit"
  | "manual-approval"
  | "traffic-control"
  | "system"
  | "audits"
  | "statistics"
  | "telemetry"
  | "telemetry-annotations"
  | "applications"
  | "organizations"
  | "organizations-create"
  | "organizations-details"
  | "organizations-details-applications"
  | "organizations-details-users"
  | "organizations-details-subs"
  | "vehicle-registry";

const persistentQueryKeys = ["embedded", "theme", "lang"] as const;

type MapViewStateRef = ReturnType<typeof useMapViewState>;

interface NavigationState {
  mapViewState: MapViewStateRef;
  showMobileSidebar: Ref<boolean>;
  loadingViews: Ref<Set<ViewType>>;
  viewKey: Ref<number>;
}

const createNavigationState = (): NavigationState => ({
  mapViewState: useMapViewState(),
  showMobileSidebar: ref(false),
  loadingViews: ref<Set<ViewType>>(new Set()),
  viewKey: ref<number>(Date.now())
});

let sharedNavigationState: NavigationState | null = null;

const getNavigationState = () => {
  sharedNavigationState ??= createNavigationState();
  return sharedNavigationState;
};

export function useNavigation() {
  const state = getNavigationState();
  let route: RouteLocationNormalizedLoaded | undefined;
  let router: Router | undefined;

  try {
    route = useRouteContext();
    router = useRouterContext();
  } catch {
    console.warn("Router not initialized, using fallback navigation");
  }

  const activeView = computed<ViewType>(() => {
    const mapView = deriveMapView(route, state.mapViewState);
    if (mapView) {
      return mapView;
    }
    if (!route) {
      return determineViewFromPath(window.location.pathname);
    }
    const name = route.name as string;
    return (name as ViewType) || "home";
  });

  const routeParams = computed(() => (route ? (route.params as Record<string, string>) : {}));
  const resolvePersistentQuery = (): LocationQueryRaw | undefined => {
    if (!router) {
      return undefined;
    }
    const currentQuery = router.currentRoute.value.query;
    const query: LocationQueryRaw = {};
    persistentQueryKeys.forEach(key => {
      const value = currentQuery[key];
      if (value !== undefined) {
        query[key] = value;
      }
    });
    return Object.keys(query).length > 0 ? query : undefined;
  };

  const navigateTo = async (
    view: ViewType,
    params?: Record<string, string>,
    options?: { query?: Record<string, string | undefined>; hash?: string; replace?: boolean }
  ) => {
    state.showMobileSidebar.value = false;
    state.viewKey.value = Date.now();

    if (!router) {
      console.error("[Navigation] Router not initialized. Unable to navigate.");
      return;
    }

    try {
      console.log("[Navigation] Navigating to:", view, "with params:", params, "and options:", options);

      const persistentQuery = resolvePersistentQuery();
      const mergedQueryObject =
        options?.query || persistentQuery
          ? Object.fromEntries(
              Object.entries({
                ...(persistentQuery ?? {}),
                ...(options?.query ?? {})
              }).filter(([, value]) => value !== undefined && value !== null)
            )
          : undefined;
      const mergedQuery =
        mergedQueryObject && Object.keys(mergedQueryObject).length > 0 ? mergedQueryObject : undefined;

      const currentRoute = router.currentRoute.value;
      if (currentRoute.name === view) {
        const currentParams = currentRoute.params as Record<string, string>;
        const sameParams = JSON.stringify(currentParams) === JSON.stringify(params ?? {});
        const targetQuery = mergedQuery ?? {};
        const sameQuery = JSON.stringify(currentRoute.query ?? {}) === JSON.stringify(targetQuery);
        const sameHash = (options?.hash ?? "") === (currentRoute.hash ?? "");
        if (sameParams && sameQuery && sameHash) {
          console.log("[Navigation] Already on target route, refreshing view key");
          state.viewKey.value = Date.now();
          return;
        }
      }

      console.log("[Navigation] Executing router.push");

      if (options?.replace) {
        await router.replace({
          name: view,
          params: params ?? {},
          query: mergedQuery,
          hash: options?.hash
        });
      } else {
        await router.push({
          name: view,
          params: params ?? {},
          query: mergedQuery,
          hash: options?.hash
        });
      }
      console.log("[Navigation] Navigation successful");
    } catch (error) {
      console.error("[Navigation] Navigation error:", error);
      throw error;
    }
  };

  const toggleMobileSidebar = () => {
    state.showMobileSidebar.value = !state.showMobileSidebar.value;
  };

  const closeMobileSidebar = () => {
    state.showMobileSidebar.value = false;
  };

  const openMobileSidebar = () => {
    state.showMobileSidebar.value = true;
  };

  const setViewLoading = (view: ViewType, isLoading: boolean) => {
    const nextSet = new Set(state.loadingViews.value);
    if (isLoading) {
      nextSet.add(view);
    } else {
      nextSet.delete(view);
    }
    state.loadingViews.value = nextSet;
  };

  const isViewLoading = (view: ViewType) => {
    return state.loadingViews.value.has(view);
  };

  const updateUrlParams = (params?: Record<string, string>) => {
    if (!params || Object.keys(params).length === 0) {
      return;
    }

    if (!router) {
      console.error("Router not initialized. Unable to update URL params.");
      return;
    }

    void router.replace({
      name: activeView.value,
      params,
      query: resolvePersistentQuery()
    });
  };

  if (route) {
    watch(
      () => route.path,
      () => {
        state.showMobileSidebar.value = false;
      }
    );
  }

  return {
    activeView,
    routeParams,
    showMobileSidebar: state.showMobileSidebar,
    viewKey: state.viewKey,
    navigateTo,
    updateUrlParams,
    toggleMobileSidebar,
    openMobileSidebar,
    closeMobileSidebar,
    setViewLoading,
    isViewLoading
  };
}

function determineViewFromPath(pathname: string): ViewType {
  const segments = pathname.split("/").filter(Boolean);
  const accountIndex = segments.lastIndexOf("dashboard");

  if (accountIndex === -1 || accountIndex === segments.length - 1) {
    return "home";
  }

  const viewSegment = segments[accountIndex + 1]?.toLowerCase();
  const subSegment = segments[accountIndex + 2]?.toLowerCase();

  if (viewSegment === "subscriptions" && subSegment === "shared") {
    return "subscription-shared-details";
  }
  if (viewSegment === "subscriptions" && subSegment === "welcometemplatemanagerinvitation") {
    return "template-manager-invitation";
  }
  if (viewSegment === "subscriptions" && subSegment === "shareduserinvitation") {
    return "shared-user-invitation";
  }
  if (viewSegment === "subscriptions" && subSegment) {
    return "subscription-details";
  }
  if (viewSegment === "subscription" && subSegment === "purchase") {
    return "subscription-purchase";
  }
  if (viewSegment === "remit") {
    return "remit";
  }
  if (viewSegment === "youpark") {
    return "youpark";
  }
  if (viewSegment === "organizations" && subSegment === "new") {
    return "organizations-create";
  }
  if (viewSegment === "organizations" && subSegment) {
    return "organizations-details";
  }

  const segmentToView: Record<string, ViewType> = {
    "sign-in": "sign-in",
    map: "map",
    "map-parking": "map-parking",
    "map-toll": "map-toll",
    parkering: "map-parking",
    bomvei: "map-toll",
    profile: "profile",
    payment: "payment",
    vehicles: "vehicles",
    "payment-sources": "payment-sources",
    "customer-service": "customer-service",
    tour: "tour",
    faq: "faq",
    subscriptions: "subscriptions",
    welcometemplatemanagerinvitation: "template-manager-invitation",
    shareduserinvitation: "shared-user-invitation",
    coupons: "coupons",
    bookings: "bookings",
    receipts: "receipts",
    "add-vehicle": "add-vehicle",
    "edit-vehicle": "edit-vehicle",
    "add-payment-source": "add-payment-source",
    "edit-payment-source": "edit-payment-source",
    remit: "remit",
    youpark: "youpark",
    "cameras-overview": "cameras-overview",
    "manual-approval": "manual-approval",
    "traffic-control": "traffic-control",
    system: "system",
    audits: "audits",
    statistics: "statistics",
    telemetry: "telemetry",
    applications: "applications",
    organizations: "organizations"
  };

  return segmentToView[viewSegment] || "home";
}

function deriveMapView(route?: RouteLocationNormalizedLoaded, mapViewState?: MapViewStateRef): ViewType | null {
  if (!route) {
    return null;
  }
  const routeName = typeof route.name === "string" ? route.name : "";
  const mapRoutes = new Set(["map", "map-parking", "map-toll", "map-area", "map-area-details", "map-lot-details"]);
  if (!mapRoutes.has(routeName)) {
    return null;
  }
  const filterQuery = typeof route.query.filter === "string" ? route.query.filter : null;
  if (filterQuery === "toll") {
    return "map-toll";
  }
  if (filterQuery === "parking") {
    return "map-parking";
  }
  if (routeName === "map-toll") {
    return "map-toll";
  }
  if (routeName === "map-parking") {
    return "map-parking";
  }
  if (mapViewState && (mapViewState.value === "map-parking" || mapViewState.value === "map-toll")) {
    return mapViewState.value;
  }
  return "map";
}
